import {
    Box,
    Collapse,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    styled,
    SvgIcon,
    Toolbar,
    Typography
} from "@mui/material";
import {Fragment, useState} from "react";
import {Link, Navigate, Outlet, useLocation, useNavigate} from "react-router-dom";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Cookies from "js-cookie";
import {LoadingComponent} from "../util/loading";
import {useLogOut} from "../routes/api";
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import VideocamIcon from '@mui/icons-material/Videocam';
import WysiwygIcon from '@mui/icons-material/Wysiwyg';
import FilterIcon from '@mui/icons-material/Filter';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import SettingsApplicationsRoundedIcon from '@mui/icons-material/SettingsApplicationsRounded';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DashboardIcon from '@mui/icons-material/Dashboard';
import {ReactComponent as UnitIcon} from '../assets/icon/unit_icon.svg';
import Tooltip from "@mui/material/Tooltip";

import logo from "../assets/logo/as_logo_small_w.png";
import bg from "../assets/logo/as_logo_med_10.png";
import ContactUs from "./contactUs";
import {ExpandLess, ExpandMore} from "@mui/icons-material";

const navigateItems = [
    {text: 'Dashboard', use_permission: 'unit', path: '/', icon: <DashboardIcon/>},
    {
        text: 'Units',
        use_permission: 'unit',
        path: '/unit',
        icon: <SvgIcon><UnitIcon/></SvgIcon>,
        sublist: [{text: 'Cameras', use_permission: 'unit', path: '/camera', icon: <VideocamIcon/>}]
    },
    {text: 'Firmware', use_permission: 'firmware', path: '/firmware', icon: <WysiwygIcon/>},
    {text: 'AI Model', use_permission: 'aimodel', path: '/aimodel', icon: <FilterIcon/>},
    {
        text: 'Application',
        use_permission: 'application',
        path: '/application',
        icon: <SettingsApplicationsRoundedIcon/>
    },
    {text: 'Account', use_permission: 'account', path: '/account', icon: <AccountBoxIcon/>},
    {text: 'Organization', use_permission: 'organization', path: '/organization', icon: <AccountBalanceIcon/>},
    {text: 'FAQ', use_permission: 'unit', path: '/help', icon: <HelpCenterIcon/>},
]

const drawerWidth = 200;

const openedMixin = (theme) => ({
    width: `${drawerWidth}px`,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiPaper-root': {
            backgroundColor: '#385480',
        },
        variants: [
            {
                props: ({open}) => open,
                style: {
                    ...openedMixin(theme),
                    '& .MuiDrawer-paper': openedMixin(theme),
                },
            },
            {
                props: ({open}) => !open,
                style: {
                    ...closedMixin(theme),
                    '& .MuiDrawer-paper': closedMixin(theme),
                },
            },
        ],

    }),
);

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({theme, open}) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const DrawerListItemButton = styled(ListItemButton)(() => ({
    '&.Mui-selected': {
        backgroundColor: '#6884b0',
        '&:hover': {
            backgroundColor: '#7894c0',
        },
    },
    '&:hover': {
        backgroundColor: '#486490',
    },
}))

const Main = styled(Box, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        // flexGrow: 1,
        padding: theme.spacing(3),
        marginTop: 25,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: open ? `${drawerWidth}px` : `calc(${theme.spacing(7)} + 1px)`,
        width: open ? `calc(100% - ${drawerWidth}px)` : `calc( 100% -${theme.spacing(7)} - 1px)`,
        [theme.breakpoints.up('sm')]: {
            marginLeft: open ? `${drawerWidth}px` : `calc(${theme.spacing(8)} + 1px)`,
            width: open ? `calc(100% - ${drawerWidth}px)` : `calc( 100% -${theme.spacing(8)} - 1px)`,

        },
        display: 'flex',
        minHeight: "100vh",
        alignItems: 'center',
        flexDirection: 'column',
        backgroundImage: `url(${bg})`,
        backgroundSize: 'auto',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        backgroundPosition: '95% 95%',

        ...(open && {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
        }),
    }),
);

export default function TopToolBar() {
    const token = Cookies.get('token');
    const username = Cookies.get('username');
    const permissions = Cookies.get('permissions') ?
        JSON.parse(Cookies.get('permissions')) : {};
    const webAdmin = Cookies.get('webAdmin') === 'T';

    const [anchorElUser, setAnchorElUser] = useState(null);
    const [clicked, setClicked] = useState(!!token);
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(false);

    const [openStates, setOpenStates] = useState({
        Units: false,
    })

    const [cameraAutoRefresh, setCameraAutoRefresh] = useState(sessionStorage.getItem('refresh') === 'T');

    const navigate = useNavigate();
    const location = useLocation();
    const {handleCall} = useLogOut(token);

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleLogout = async () => {
        setLoading(true);
        setClicked(false);
        Cookies.remove('token');
        Cookies.remove('permissions');
        Cookies.remove('username');
        await handleCall();
        setLoading(false);
    }

    const handleLogin = () => {
        navigate('/login');
    }

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setOpen(open);
    };

    const selected = (item) => {
        if (location.pathname === '/' && item.text === 'Dashboard') {
            return true;
        }
        if (location.pathname.split('/')[1] === 'camera' && item.path.split('/')[1] === 'unit') {
            return true;
        }
        return (location.pathname.split('/')[1] === item.path.split('/')[1]);
    }

    const handleDropdownClick = (key) => {
        setOpenStates({...openStates, [key]: !openStates[key]});
    }

    const handleSetCameraAutoRefresh = (e) => {
        e.preventDefault();
        setCameraAutoRefresh(e.target.checked);
        sessionStorage.setItem('refresh', e.target.checked ? 'T' : 'F')
    }

    function ListObject({item, index, width, sx}) {
        return <DrawerListItemButton
            key={index}
            component={Link}
            to={item.path}
            selected={selected(item)}
            state={{from: location}}
            sx={{
                width: width ? width : 'auto',
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                color: '#e9f9fe',
            }}
        >
            <ListItemIcon
                sx={{
                    minWidth: 0,
                    mr: open ? 2 : 'auto',
                    justifyContent: 'center',
                    color: '#e9f9fe',
                    ...sx
                }}
            >
                {open ?
                    item.icon :
                    <Tooltip title={item.text} arrow>{item.icon}</Tooltip>}
            </ListItemIcon>
            <ListItemText primary={item.text}
                          sx={{ml: open ? 1 : 3, opacity: open ? 1 : 0}}/>
        </DrawerListItemButton>
    }

    if (loading) return <LoadingComponent/>;

    if (username && token && permissions) {
        return <Box sx={{display: 'flex', width: '100%'}}>
            <AppBar position="fixed" open={open}>
                <Toolbar>
                    <Drawer
                        variant='permanent'
                        open={open}
                        onClose={toggleDrawer(false)}
                    >
                        <DrawerHeader>
                            <IconButton onClick={toggleDrawer(!open)}>
                                {open ? <ChevronLeftIcon sx={{color: '#A8C4F0'}}/> :
                                    <ChevronRightIcon sx={{color: '#A8C4F0'}}/>}
                            </IconButton>
                        </DrawerHeader>
                        <Box
                            sx={{width: 250}}
                            role="presentation"
                            onKeyDown={toggleDrawer(false)}
                        >
                            <List>
                                {navigateItems.filter((item) => (webAdmin || permissions[item.use_permission]?.view)).map((item, index) => (
                                        <Fragment key={item.text}>
                                            <ListItem key={item.text} disablePadding sx={{display: 'block'}}>
                                                <Box display="flex" width="100%" justifyContent="space-between"
                                                     alignItems="center">
                                                    <ListObject item={item} index={index} width={80}/>
                                                    {(item.sublist && open) &&
                                                        <DrawerListItemButton
                                                            onClick={() => handleDropdownClick(item.text)}
                                                            key={`${item.text}_dropdown}`}
                                                            selected={selected(item)}
                                                            sx={{
                                                                minHeight: 48,
                                                                width: 3,
                                                                justifyContent: 'flex-start',
                                                                color: '#e9f9fe'
                                                            }}>
                                                            {openStates[item.text] ? <ExpandLess color='#e9f9fe'/> :
                                                                <ExpandMore color='#e9f9fe'/>}
                                                        </DrawerListItemButton>
                                                    }
                                                </Box>
                                            </ListItem>
                                            {
                                                item.sublist &&
                                                <Collapse in={openStates[item.text]} timeout="auto" unmountOnExit>
                                                    <List component="div" disablePadding>
                                                        {item.sublist.map((item, index) => (
                                                            <ListItem key={item.text} disablePadding
                                                                      sx={{display: 'block'}}>
                                                                <Box display="flex" width="100%"
                                                                     justifyContent="space-between"
                                                                     alignItems="center">
                                                                    <ListObject item={item} index={index}
                                                                                sx={{paddingLeft: open ? 3 : 0}}/>
                                                                </Box>
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Collapse>
                                            }
                                        </Fragment>
                                    )
                                )}
                            </List>
                        </Box>
                        <Box sx={{flexGrow: 1}}/>
                        {/*<Box sx={{*/}
                        {/*    display: 'flex',*/}
                        {/*    justifyContent: open ? 'initial' : 'center',*/}
                        {/*    alignItems: 'center',*/}
                        {/*    mb: 1*/}
                        {/*}}>*/}
                        {/*    {open ?*/}
                        {/*        <Switch*/}
                        {/*            color='toolbarSuccess'*/}
                        {/*            checked={cameraAutoRefresh}*/}
                        {/*            onChange={handleSetCameraAutoRefresh}*/}
                        {/*        /> :*/}
                        {/*        <Tooltip title={'Camera Auto Refresh Page'}>*/}
                        {/*            <Switch*/}
                        {/*                color='toolbarSuccess'*/}
                        {/*                sx={{mr: open ? 3 : 'auto'}}*/}
                        {/*            />*/}
                        {/*        </Tooltip>*/}
                        {/*    }*/}
                        {/*    <Typography color='#e9f9fe' sx={{ml: open ? 1 : 3, opacity: open ? 1 : 0}}>*/}
                        {/*        Camera Auto<br/>Refresh Page*/}
                        {/*    </Typography>*/}
                        {/*</Box>*/}
                    </Drawer>
                    <Link to="/">
                        <Box
                            component="img"
                            src={logo}
                            alt="AglaiaSense"
                            sx={{height: 40, marginRight: 2}}
                        />
                    </Link>
                    <Box sx={{flexGrow: 1}}/>
                    {/*<Typography*/}
                    {/*    component={Link}*/}
                    {/*    to={`${BASE_URL}/api/docs/`}*/}
                    {/*    target="_blank"*/}
                    {/*    rel="noopener noreferrer"*/}
                    {/*    variant="subtitle1"*/}
                    {/*    style={{textDecoration: 'none', color: 'inherit'}}*/}
                    {/*    sx={{mr: 2}}*/}
                    {/*    margin*/}
                    {/*>*/}
                    {/*    Developer Portal*/}
                    {/*</Typography>*/}

                    {!clicked ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleLogin}
                        >
                            Login
                        </Button>
                    ) : (<Button
                            onClick={handleOpenUserMenu}
                            key='Avatar'
                            sx={{typography: "subtitle1", textTransform: "none", color: 'white'}}
                            startIcon={<Avatar
                                alt={username}
                                children={username[0].toUpperCase()}
                                sx={{bgcolor: '#19AB91'}}/>}
                        >
                            {username}
                        </Button>
                    )}
                    <Box sx={{flexGrow: 0}}>
                        <Menu
                            sx={{mt: '45px'}}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            <MenuItem key="Logout" onClick={handleLogout}>
                                <Typography
                                    textAlign="center">Logout</Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Main open={open}>
                <Outlet context={[cameraAutoRefresh, setCameraAutoRefresh]}/>
                <Box sx={{mt: 25, mb: 5, width: '100%'}}>
                    <ContactUs/>
                </Box>
            </Main>
            {/*<DeviceUpdateAlert/>*/}
        </Box>
    } else {
        return <Navigate to="/login" replace/>
    }
}